import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { staffService } from '../../services/staffService';
import { FiPlus, FiEdit, FiTrash2, FiUserCheck, FiEye, FiSearch } from 'react-icons/fi';

const StaffList = () => {
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await staffService.getAll();
      setStaff(data || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await staffService.delete(id);
        await loadData();
      } catch (error) {
        alert('Error deleting staff: ' + error.message);
      }
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Active',
      'inactive': 'Inactive',
      'on-leave': 'On Leave'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'on-leave': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredStaff = Array.isArray(staff) 
    ? staff.filter(member => {
        const matchesSearch = member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              member.phone?.includes(searchTerm) ||
                              member.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
        return matchesSearch && matchesStatus;
      })
    : [];

  //Stats
  const totalStaff = Array.isArray(staff) ? staff.length : 0;
  const activeStaff = Array.isArray(staff) 
    ? staff.filter(s => s.status === 'active').length 
    : 0;
  const onLeaveStaff = Array.isArray(staff) 
    ? staff.filter(s => s.status === 'on-leave').length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading staff...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Staff</h1>
        <Link
          to="/staff/new"
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <FiPlus size={20} />
          <span>Add Staff</span>
        </Link>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Total</p>
          <p className="text-lg font-bold text-[var(--text-primary)]">{totalStaff}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Active</p>
          <p className="text-lg font-bold text-green-600">{activeStaff}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">On Leave</p>
          <p className="text-lg font-bold text-yellow-600">{onLeaveStaff}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={16} />
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="on-leave">On Leave</option>
        </select>
      </div>

      {/* Compact Cards Grid -pakai poto */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredStaff.length === 0 ? (
          <div className="col-span-full text-center py-12 text-[var(--text-secondary)]">
            <FiUserCheck size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No staff found</p>
            <p className="text-sm">Click "Add Staff" to create one</p>
          </div>
        ) : (
          filteredStaff.map((member) => (
            <div 
              key={member.id} 
              className="bg-[var(--bg-primary)] p-3 rounded-lg shadow-sm border border-[var(--border-color)] hover:shadow-md transition-shadow"
            >
     
              {/* FOTO SNIPPET + DATA STAFF */}
              <div className="flex items-start space-x-3">
                
                {/*FOTO SNIPPET*/}
                <div className="flex-shrink-0">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-10 h-10 rounded-full object-cover border border-[var(--border-color)]"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs border border-[var(--border-color)]">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* ---DATA STAFF*/}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-sm text-[var(--text-primary)] truncate">
                      {member.name}
                    </h3>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${getStatusColor(member.status)} flex-shrink-0`}>
                      {getStatusLabel(member.status)}
                    </span>
                  </div>
                  
                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs text-[var(--text-secondary)] flex items-center space-x-1">
                      <span>📱</span>
                      <span>{member.phone}</span>
                    </p>
                    {member.email && (
                      <p className="text-xs text-[var(--text-secondary)] flex items-center space-x-1 truncate">
                        <span>✉️</span>
                        <span className="truncate">{member.email}</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <span className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-800 rounded-full">
                      {member.level}
                    </span>
                    <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded-full">
                      {member.commission}%
                    </span>
                    {member.joinDate && (
                      <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-800 rounded-full">
                        {new Date(member.joinDate).toLocaleDateString('id-ID')}
                      </span>
                    )}
                  </div>

                  {/* Skills - compact */}
                  {member.skills && member.skills.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-0.5">
                      {member.skills.slice(0, 2).map((skill, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 text-[9px] bg-gray-100 text-gray-700 rounded-full">
                          {skill}
                        </span>
                      ))}
                      {member.skills.length > 2 && (
                        <span className="px-1.5 py-0.5 text-[9px] bg-gray-100 text-gray-700 rounded-full">
                          +{member.skills.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* --- ACTIONS*/}
                <div className="flex space-x-0.5 ml-2 flex-shrink-0">
                  <Link
                    to={`/staff/${member.id}`}
                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                    title="View Details"
                  >
                    <FiEye size={14} />
                  </Link>
                  <Link
                    to={`/staff/edit/${member.id}`}
                    className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition-colors"
                    title="Edit"
                  >
                    <FiEdit size={14} />
                  </Link>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>

              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StaffList;